/**
 * PendingInvitations — the Hub dashboard "you've been invited" inbox
 * (ADR-0010 M5c-3). Lists pending cross-product invitations from the enriched
 * `/v1/invitations/me` and lets the owner Accept / Decline in one click
 * (token-less, email-matched). Renders nothing when there are no pending invites.
 */

import React, { type CSSProperties } from "react";
import { ProductIcon } from "./ProductIcon.js";
import { PRODUCT_COLORS } from "../config/products.js";
import { productById, resourceNoun } from "../lib/productResource.js";
import { useInvitationsInbox } from "../lib/useInvitationsInbox.js";
import { useTranslation } from "react-i18next";

const T = {
  navy: "#261F59",
  ink: "#2b2e44",
  muted: "#6b7084",
  faint: "#9096a8",
  border: "#E3EBF6",
  white: "#fff",
  subtle: "#F7F6FB",
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
} as const;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function PendingInvitations() {
  const { t } = useTranslation();
  const { invites, accept, decline, busyId } = useInvitationsInbox();

  if (invites.length === 0) return null;

  return (
    <section aria-label={t("pendingInvites.ariaLabel")} style={cardStyle}>
      <div style={headerStyle}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.navy, letterSpacing: ".02em" }}>
          {t("pendingInvites.heading")}
        </span>
        <span style={countPill}>{invites.length}</span>
      </div>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {invites.map((inv) => {
          const product = productById(inv.productId);
          const accent = PRODUCT_COLORS[inv.productId]?.accent ?? "#351DBD";
          const accentLight = PRODUCT_COLORS[inv.productId]?.accentLight ?? "#EDE9FC";
          const busy = busyId === inv.invitationId;
          const noun = resourceNoun(inv.productId, inv.resourceType);
          return (
            <li key={inv.invitationId} style={rowStyle}>
              <span style={{ ...iconWrap, background: accentLight }}>
                <ProductIcon type={product?.icon ?? "calendar"} color={accent} size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={lineStyle} title={inv.resourceLabel ?? undefined}>
                  <strong style={{ color: T.ink }}>{inv.inviterName || t("pendingInvites.inviterFallback")}</strong>
                  {t("pendingInvites.invitedYouTo")}
                  <strong style={{ color: T.ink }}>{inv.resourceLabel || t("pendingInvites.resourceFallback", { noun })}</strong>
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 1 }}>
                  {t("pendingInvites.roleAccess", { product: product?.name ?? inv.productId, role: cap(inv.role) })}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void accept(inv)}
                  style={{ ...acceptBtn, background: accent, opacity: busy ? 0.6 : 1 }}
                >
                  {busy ? "…" : t("pendingInvites.acceptButton")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void decline(inv)}
                  style={{ ...declineBtn, opacity: busy ? 0.6 : 1 }}
                >
                  {t("pendingInvites.declineButton")}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const cardStyle: CSSProperties = {
  background: T.white,
  border: `1px solid ${T.border}`,
  borderRadius: 14,
  padding: "14px 16px",
  marginBottom: 20,
  fontFamily: T.font,
  boxShadow: "0 1px 3px rgba(53,29,189,0.04), 0 2px 8px rgba(53,29,189,0.04)",
};
const headerStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 };
const countPill: CSSProperties = { display: "inline-flex", minWidth: 20, height: 20, padding: "0 6px", alignItems: "center", justifyContent: "center", borderRadius: 999, background: "#EDE9FC", color: "#351DBD", fontSize: 12, fontWeight: 700 };
const rowStyle: CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: `1px solid ${T.subtle}` };
const iconWrap: CSSProperties = { width: 34, height: 34, borderRadius: 9, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
const lineStyle: CSSProperties = { fontSize: 13, color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const acceptBtn: CSSProperties = { minHeight: 36, padding: "6px 14px", borderRadius: 8, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" };
const declineBtn: CSSProperties = { minHeight: 36, padding: "6px 14px", borderRadius: 8, color: T.ink, background: T.white, border: `1px solid ${T.border}`, fontSize: 13, fontWeight: 600, cursor: "pointer" };
